import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function PrivacyPage() {
  const navigate = useNavigate();
  const appName = 'Diamond Scorer';
  const companyName = 'Diamond Scorer';
  const effectiveDate = 'February 1, 2026';

  return (
    <div className="min-h-screen bg-stone-900 text-stone-300 p-4">
      <div className="max-w-2xl mx-auto">
        <Button variant="ghost" className="text-stone-400 mb-4" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>

        <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Privacy Policy</h1>
        <p className="text-stone-500 text-sm mb-6">Effective Date: {effectiveDate}</p>

        <div className="space-y-6 text-sm leading-relaxed">
          <section>
            <h2 className="text-white font-bold text-base mb-2">1. Information We Collect</h2>
            <p>We collect the following types of information:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong className="text-white">Account Information:</strong> Username, password (stored encrypted), phone number (optional, for account recovery).</li>
              <li><strong className="text-white">Team & Player Data:</strong> Team names, player names, jersey numbers, positions, game scores, play-by-play data, and statistics as entered by coaches.</li>
              <li><strong className="text-white">Photos & Images:</strong> Lineup card photos uploaded for scanning. These are processed for text extraction and are not stored permanently on our servers after processing.</li>
              <li><strong className="text-white">Messages:</strong> Team communications sent through the in-app messaging feature.</li>
              <li><strong className="text-white">Device Information:</strong> We may collect basic device information for app functionality, including push notification tokens.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">2. How We Use Your Information</h2>
            <ul className="list-disc ml-6 space-y-1">
              <li>To provide and maintain the App's services (scoring, statistics, lineup management)</li>
              <li>To enable team communication between coaches and parents</li>
              <li>To send push notifications for game updates and scoring alerts (with your consent)</li>
              <li>To process lineup card images using AI-powered text recognition</li>
              <li>To generate game summaries and player report cards</li>
              <li>To improve the App's features and user experience</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">3. Camera Usage</h2>
            <p>The App uses your device's camera solely for the following purposes:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Scanning physical lineup cards to digitize player names and positions</li>
              <li>Capturing team photos for messaging and game highlights</li>
            </ul>
            <p className="mt-2">Camera access is only activated when you explicitly initiate a scan or photo action. Images taken for lineup scanning are sent to our secure servers for AI processing and are not retained after the scan is complete. You can revoke camera permissions at any time through your device settings.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">4. Push Notifications</h2>
            <p>With your consent, we send push notifications for:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Live scoring updates during games</li>
              <li>Urgent team messages from coaches</li>
              <li>Game reminders and schedule updates</li>
            </ul>
            <p className="mt-2">You may disable push notifications at any time through your device settings or within the App.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">5. Data Sharing</h2>
            <p>We do not sell, trade, or rent your personal information to third parties. We may share data in the following limited circumstances:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong className="text-white">Within Your Team:</strong> Game data, lineups, and statistics are visible to coaches and parents within the same team.</li>
              <li><strong className="text-white">Public Game Views:</strong> Live scores and play-by-play may be accessible via shareable links created by coaches.</li>
              <li><strong className="text-white">Service Providers:</strong> We use third-party services (cloud hosting, AI processing) to operate the App. These providers are bound by data protection agreements.</li>
              <li><strong className="text-white">Legal Requirements:</strong> We may disclose information when required by law or to protect our rights.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">6. Children's Privacy</h2>
            <p>We comply with the Children's Online Privacy Protection Act (COPPA). We do not knowingly collect personal information directly from children under 13. Player information for minors is entered by coaches and parents who represent they have appropriate authority and consent. Parents may contact us to review, modify, or delete their child's information.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">7. Data Security</h2>
            <p>We implement industry-standard security measures to protect your data, including:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Encrypted password storage (bcrypt hashing)</li>
              <li>Secure HTTPS connections for all data transmission</li>
              <li>Access controls limiting data visibility to authorized team members</li>
            </ul>
            <p className="mt-2">While we strive to protect your information, no method of electronic storage is 100% secure. We cannot guarantee absolute security.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">8. Data Retention</h2>
            <p>We retain your data for as long as your account is active. Game data and statistics are kept to provide season-long analytics. You may request deletion of your account and associated data at any time by contacting support.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">9. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Access and review your personal data</li>
              <li>Correct inaccurate information</li>
              <li>Request deletion of your account and data</li>
              <li>Opt out of push notifications</li>
              <li>Revoke camera and other device permissions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify users of material changes through the App. Continued use after changes constitutes acceptance.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">11. Contact</h2>
            <p>If you have questions about this Privacy Policy or wish to exercise your rights, please contact us through the App's support features.</p>
          </section>
        </div>

        <div className="border-t border-stone-700 mt-8 pt-4 text-center text-stone-600 text-xs">
          {companyName} &copy; {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}
